import { TasklistStartIcon } from '@navikt/aksel-icons';
import dayjs from 'dayjs';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { BodyLong, VStack } from '@navikt/ds-react';

import { RhfDatepicker } from '@navikt/fp-form-hooks';
import { OmBarnetPlanlegger } from '@navikt/fp-types';
import { BluePanel, Infobox } from '@navikt/fp-ui';
import {
    isAfterOrSame,
    isAfterOrSameAsSixMonthsAgo,
    isBeforeOrSame,
    isBeforeTodayOrToday,
    isRequired,
    isValidDate,
} from '@navikt/fp-validation';

type Props = {
    erAlenesøker: boolean;
    erOmBarnetPlanleggerIkkeOppgittFraFør: boolean;
    antallBarn?: string;
};

export const Adopsjon = ({ erAlenesøker, erOmBarnetPlanleggerIkkeOppgittFraFør, antallBarn }: Props) => {
    const intl = useIntl();

    const flereBarn = antallBarn === '3' || antallBarn === '2';

    const formMethods = useFormContext<OmBarnetPlanlegger>();
    const fødselsdato = formMethods.watch('fødselsdato');
    const overtakelsesdato = formMethods.watch('overtakelsesdato');

    return (
        <>
            <BluePanel isDarkBlue={erOmBarnetPlanleggerIkkeOppgittFraFør} shouldFadeIn>
                <VStack gap="space-32">
                    <RhfDatepicker
                        name="overtakelsesdato"
                        control={formMethods.control}
                        label={<FormattedMessage id="Adopsjon.Overtakelsesdato" values={{ erAlenesøker, flereBarn }} />}
                        minDate={dayjs().subtract(6, 'month')}
                        showMonthAndYearDropdowns
                        validate={[
                            isRequired(intl.formatMessage({ id: 'Adopsjon.Required' }, { erAlenesøker, flereBarn })),
                            isValidDate(intl.formatMessage({ id: 'ValidationMessage.ValidDate' })),
                            isAfterOrSameAsSixMonthsAgo(
                                intl.formatMessage({
                                    id: 'ValidationMessage.OlderThan6months',
                                }),
                            ),
                            isAfterOrSame(
                                intl.formatMessage({ id: 'ValidationMessage.FødselsdatoMåVæreFørOmsorgovertakelse' }),
                                fødselsdato,
                            ),
                            isBeforeOrSame(
                                intl.formatMessage({
                                    id: 'ValidationMessage.OmsorgsovertakelseKanIkkeVæreLengerEnn15ÅrEtterFødsel',
                                }),
                                fødselsdato ? dayjs(fødselsdato).add(15, 'years') : fødselsdato,
                            ),
                        ]}
                    />
                    <RhfDatepicker
                        name="fødselsdato"
                        control={formMethods.control}
                        label={<FormattedMessage id="Adopsjon.Fødselsdato" values={{ flereBarn }} />}
                        minDate={dayjs().subtract(15, 'years')}
                        maxDate={dayjs()}
                        showMonthAndYearDropdowns
                        validate={[
                            isRequired(intl.formatMessage({ id: 'Fødselsdato.Required' })),
                            isValidDate(intl.formatMessage({ id: 'ValidationMessage.ValidDate' })),
                            isBeforeTodayOrToday(
                                intl.formatMessage({
                                    id: 'ValidationMessage.IdagEllerTidligere',
                                }),
                            ),
                        ]}
                    />
                </VStack>
            </BluePanel>
            {overtakelsesdato && (
                <Infobox
                    header={
                        <FormattedMessage id="OmBarnetSteg.Adopsjon.ForeldrepengerInfo" values={{ erAlenesøker }} />
                    }
                    icon={
                        <TasklistStartIcon
                            height={24}
                            width={24}
                            color="var(--ax-bg-success-strong)"
                            fontSize="1.5rem"
                            aria-hidden
                        />
                    }
                    shouldFadeIn
                    color="green"
                >
                    <VStack gap="space-8">
                        <BodyLong>
                            <FormattedMessage
                                id="OmBarnetSteg.Adopsjon.ForeldrepengerInfoTekst.KanSøke"
                                values={{
                                    erAlenesøker,
                                }}
                            />
                        </BodyLong>
                        <BodyLong>
                            <FormattedMessage
                                id="OmBarnetSteg.Adopsjon.ForeldrepengerInfoTekst.FireUkerFør"
                                values={{
                                    erAlenesøker,
                                }}
                            />
                        </BodyLong>
                        <BodyLong>
                            <FormattedMessage
                                id="OmBarnetSteg.Adopsjon.ForeldrepengerInfoTekst.KanStarte"
                                values={{
                                    erAlenesøker,
                                }}
                            />
                        </BodyLong>
                    </VStack>
                </Infobox>
            )}
        </>
    );
};
